import type { OpCodesSettingsResponse, OperationCode } from 'types';

import clientApi from 'api/clientApi';
import { Endpoints } from 'utility/constants';
import { sanitize } from 'utility/helpers';

export async function fetchOpCodes(options: {
  page?: number;
  searchTerm?: string;
  pageLimit?: number;
  includeDisabled?: boolean;
  includePackagePrice?: boolean;
}): Promise<OpCodesSettingsResponse> {
  const params = {
    page: options.page ?? 1,
    searchTerm: options.searchTerm,
    pageLimit: options.pageLimit ?? -1,
    includeDisabled: options.includeDisabled ?? false,
    ...(options.includePackagePrice && { includePackagePrice: true }),
  };
  const response = await clientApi.get(Endpoints.opCodes, { params });
  return response.data;
}

export async function createOpCode(workingOpCode: Partial<OperationCode>): Promise<OperationCode> {
  const sanitized = sanitize(workingOpCode);

  const response = await clientApi.post(Endpoints.opCodes, {
    code: sanitized.code,
    laborHours: sanitized.laborHours,
    complaint: sanitized.complaint,
    laborTaxable: !!sanitized.laborTaxable,
    partsPrice: sanitized.partsPrice,
    partsTaxable: !!sanitized.partsTaxable,
    laborPrice: sanitized.laborPrice,
    menuPricing: !!sanitized.menuPricing,
    menuPartsPricing: !!sanitized.menuPartsPricing,
    chargeSupplies: !!sanitized.chargeSupplies,
    serviceLane: sanitized.serviceLane,
    defaultLaborRateId: workingOpCode.defaultLaborRate?.id ?? workingOpCode.defaultLaborRateId,
    serviceGroupId: sanitized.serviceGroupId,
    category: sanitized.category,
    activeInIservice: workingOpCode.activeInIservice ?? true,
  });

  return response.data;
}

export async function updateOpCode(id: number, workingOpCode: Partial<OperationCode>) {
  await clientApi.put(`${Endpoints.opCodes}/${id}`, {
    code: workingOpCode.code,
    laborHours: workingOpCode.laborHours,
    complaint: workingOpCode.complaint,
    laborTaxable: !!workingOpCode.laborTaxable,
    partsPrice: workingOpCode.partsPrice,
    partsTaxable: !!workingOpCode.partsTaxable,
    laborPrice: workingOpCode.laborPrice,
    menuPricing: !!workingOpCode.menuPricing,
    menuPartsPricing: !!workingOpCode.menuPartsPricing,
    chargeSupplies: !!workingOpCode.chargeSupplies,
    serviceLane: workingOpCode.serviceLane,
    defaultLaborRateId: workingOpCode.defaultLaborRate?.id ?? workingOpCode.defaultLaborRateId,
    serviceGroupId: workingOpCode.serviceGroupId,
    category: workingOpCode.category,
    // Send the flag as-is (no `?? true`): an omitted value must reach the API as absent so it
    // preserves the op code's current active state, rather than silently reactivating a disabled
    // code on a partial update. Unlike createOpCode, where `?? true` correctly defaults new codes.
    activeInIservice: workingOpCode.activeInIservice,
  });
}

export async function deleteOpCode(id: number) {
  await clientApi.delete(`${Endpoints.opCodes}/${id}`);
}

export async function setOpCodeActive(id: number, active: boolean): Promise<OperationCode> {
  const response = await clientApi.patch(`${Endpoints.opCodes}/${id}/active`, { active });
  return response.data;
}
